// Measurement-only instrumentation for the theme-swap re-render baseline.
// Everything here is gated behind GATSBY_PROFILE=1 so it is tree-shaken /
// no-op'd out of normal builds and trivially removable once the tokens →
// CSS-custom-properties refactor is validated against the numbers it captures.
//
// It NEVER logs (the repo enforces no-console: error) — it accumulates into a
// module-level store and exposes a tiny control API on window for the headless
// perf harness (perf-baseline/measure-rerenders.mjs) to drive.

export const THEME_PERF_ENABLED = process.env.GATSBY_PROFILE === '1'

interface PerfStore {
  // Number of React commits to the profiled subtree.
  commits: number
  // Summed React.Profiler actualDuration across those commits (ms).
  totalActualDurationMs: number
  // Component fibers that re-rendered, summed across every commit in the window
  // (includes GSAP/asset-driven commits — noisy, kept only for context).
  componentsRendered: number
  // Component fibers that re-rendered in the single largest commit. The theme
  // swap re-renders every theme consumer in one commit, so this is the
  // deterministic signal the CSS-vars refactor will shrink — unlike the noisy
  // animation/asset commits, the peak is stable run-to-run.
  peakCommitComponents: number
}

const store: PerfStore = {
  commits: 0,
  totalActualDurationMs: 0,
  componentsRendered: 0,
  peakCommitComponents: 0,
}

// Called from the React.Profiler onRender callback (ThemePerfProfiler).
export const recordCommit = (actualDuration: number) => {
  store.commits += 1
  store.totalActualDurationMs += actualDuration
}

// Called once per commit from the devtools-hook fiber counter
// (themePerfDevtoolsHook) with that commit's rendered-component count.
export const recordComponentsRendered = (count: number) => {
  store.componentsRendered += count
  if (count > store.peakCommitComponents) store.peakCommitComponents = count
}

export interface ThemePerfSnapshot {
  commits: number
  totalActualDurationMs: number
  componentsRendered: number
  peakCommitComponents: number
}

export interface ThemePerfApi {
  reset: () => void
  snapshot: () => ThemePerfSnapshot
}

const themePerfApi: ThemePerfApi = {
  reset: () => {
    store.commits = 0
    store.totalActualDurationMs = 0
    store.componentsRendered = 0
    store.peakCommitComponents = 0
  },
  snapshot: () => ({ ...store }),
}

declare global {
  interface Window {
    __themePerf?: ThemePerfApi
  }
}

// Exposes window.__themePerf for the harness. No-op unless profiling is on and
// we're on the client.
export const installThemePerfApi = () => {
  if (!THEME_PERF_ENABLED || typeof window === 'undefined') return
  window.__themePerf = themePerfApi
}
